/**
 * Authentication Service
 *
 * Implements the authentication flow:
 * 1. GET /auth/nonce - Fetch nonce from server
 * 2. Encrypt credentials with AES-256-CBC using nonce
 * 3. POST /auth/login - Send encrypted credentials
 * 4. Receive authToken (wpcid) for WebSocket registration
 *
 * Based on page-editor/src/load-test/get-token.js
 */

import { getApiBaseUrl, getAuthApiUrl } from '@/config/config'
import { clearStoredEditorZoom } from '@/lib/zoom/editor-zoom-storage'
import { clearStoredSpreadIndex } from '@/lib/editor/editor-spread-storage'
import { clearStoredPhotosTrayAlbum } from '@/lib/photos/photos-tray-album-storage'
import { clearStoredPhotosTrayShowFilename } from '@/lib/photos/photos-tray-show-filename-storage'
import { clearStoredPhotosTraySort } from '@/lib/photos/photos-tray-sort-storage'
import { logger } from '@/utils/logger'
import { clearStoredConnectionId } from '@/providers/websocket'

interface NonceResponse {
  payload: {
    nonce: string
  }
}

interface LoginResponse {
  payload: {
    authToken: string
  }
}

interface LoginCredentials {
  username: string
  password: string
}

// Storage keys
// `wpc_auth_token` is the wpcauth session id (aka wpcid) returned from `/auth/login`.
// It is exchanged for a JWT via `/authentication/token`.
const AUTH_TOKEN_KEY = 'wpc_auth_token'
const AUTH_USER_KEY = 'wpc_auth_user'
const AUTH_JWT_KEY = 'wpc_auth_jwt'
const AUTH_JWT_EXPIRY_KEY = 'wpc_auth_jwt_expiry'

interface JwtTokenResponse {
  token?: string
  tokenType?: string
  expiresIn?: number
  user?: unknown
  message?: string
}

// ---------------------------------------------------------------------------
// JWT expiry parsing
// ---------------------------------------------------------------------------

/**
 * Decode the payload of a JWT (without verification) and return the `exp`
 * claim as a Unix-epoch *millisecond* timestamp, or `null` when the token
 * cannot be parsed or has no `exp`.
 */
export function parseJwtExpiryMs(jwt: string): number | null {
  try {
    const parts = jwt.split('.')

    if (parts.length !== 3) return null

    // base64url → base64 → decode
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = atob(b64)
    const payload: { exp?: number } = JSON.parse(json)

    if (typeof payload.exp !== 'number') return null
    return payload.exp * 1000
  } catch {
    return null
  }
}

/**
 * Decode the payload of a JWT (without verification) and return the payload object,
 * or null when the token cannot be parsed.
 */
export function parseJwtPayload(jwt: string): Record<string, unknown> | null {
  try {
    const parts = jwt.split('.')

    if (parts.length !== 3) return null

    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = atob(b64)
    const payload = JSON.parse(json) as Record<string, unknown>

    return payload
  } catch {
    return null
  }
}

/**
 * Return the Planbook job ID from the stored JWT claims, or null if not present.
 * Used by the book-setting service to call Larry GET /booksettings/:jobId.
 */
export function getJobIdFromStoredJwt(): string | null {
  const jwt = getStoredJwtToken()

  if (!jwt) return null

  const payload = parseJwtPayload(jwt)

  if (!payload) return null

  const jobId = payload.jobId ?? payload.JobId

  if (typeof jobId !== 'string' || !jobId.trim()) return null

  const normalizedJobId = jobId.trim()

  if (/^0+$/.test(normalizedJobId)) return null

  return normalizedJobId
}

/**
 * Return the user ID from the stored JWT claims (`staffId`), or null if not present.
 * Matches backend `UserContext.GetUserID()` which uses StaffId.
 */
export function getUserIdFromStoredJwt(): string | null {
  const jwt = getStoredJwtToken()

  if (!jwt) return null

  const payload = parseJwtPayload(jwt)

  if (!payload) return null

  const userId = payload.staffId ?? payload.StaffId ?? payload.userId ?? payload.UserId

  if (typeof userId !== 'string' || !userId.trim()) return null

  return userId.trim()
}

/** Return the job year from the stored JWT claims (`jobYear`), or null if not present. */
export function getJobYearFromStoredJwt(): number | null {
  const jwt = getStoredJwtToken()

  if (!jwt) return null

  const payload = parseJwtPayload(jwt)

  if (!payload) return null

  const jobYear = payload.jobYear ?? payload.JobYear

  if (typeof jobYear !== 'number' || !Number.isFinite(jobYear) || jobYear <= 0) return null

  return jobYear
}

// ---------------------------------------------------------------------------
// Force-logout hook (set by AuthProvider, called by HTTP client / scheduler)
// ---------------------------------------------------------------------------

type ForceLogoutHandler = () => void
let _forceLogoutHandler: ForceLogoutHandler | null = null

export function setForceLogoutHandler(handler: ForceLogoutHandler | null): void {
  _forceLogoutHandler = handler
}

export function triggerForceLogout(): void {
  if (_forceLogoutHandler) _forceLogoutHandler()
}

// ---------------------------------------------------------------------------
// Session-ended hook (set by the embed page, called by the force-logout handler)
// ---------------------------------------------------------------------------

type SessionEndedHandler = () => void
let _sessionEndedHandler: SessionEndedHandler | null = null

/**
 * Replaces the redirect to `/login` after a force logout. Embedded (`/embed`), the host app owns
 * the session, so the embed page tells the host instead of showing Larry's login in its iframe.
 * Runs before the logout clears auth and the query cache. Pass null to restore the redirect.
 */
export function setSessionEndedHandler(handler: SessionEndedHandler | null): void {
  _sessionEndedHandler = handler
}

/** Runs the session-ended handler. False when none is set, so the caller redirects to `/login`. */
export function notifySessionEnded(): boolean {
  if (!_sessionEndedHandler) return false

  _sessionEndedHandler()
  return true
}

// ---------------------------------------------------------------------------
// Proactive token refresh scheduler
// ---------------------------------------------------------------------------

const REFRESH_THRESHOLD = 0.8 // refresh when 80% of lifetime has elapsed
const MIN_REFRESH_MS = 10_000 // never schedule sooner than 10 s

let _refreshTimerId: ReturnType<typeof setTimeout> | null = null

function clearRefreshTimer(): void {
  if (_refreshTimerId !== null) {
    clearTimeout(_refreshTimerId)
    _refreshTimerId = null
  }
}

/**
 * Schedule a proactive token refresh based on the JWT expiry.  If the token
 * is already within the refresh window it fires immediately.
 */
export function scheduleProactiveRefresh(jwt?: string): void {
  clearRefreshTimer()

  const token = jwt ?? getStoredJwtToken()

  if (!token) return

  const expiryMs = parseJwtExpiryMs(token)

  if (!expiryMs) return

  const now = Date.now()
  const lifetime = expiryMs - now

  if (lifetime <= 0) {
    // Already expired — force logout
    logger.warn('JWT already expired, forcing logout')
    triggerForceLogout()
    return
  }

  const delay = Math.max(lifetime * REFRESH_THRESHOLD, MIN_REFRESH_MS)

  logger.debug(
    `Proactive JWT refresh scheduled in ${Math.round(delay / 1000)}s ` +
      `(token expires in ${Math.round(lifetime / 1000)}s)`
  )

  _refreshTimerId = setTimeout(async () => {
    _refreshTimerId = null
    const next = await refreshJwtToken()

    if (next) {
      scheduleProactiveRefresh(next)
    } else {
      logger.warn('Proactive JWT refresh failed, forcing logout')
      triggerForceLogout()
    }
  }, delay)
}

export function stopProactiveRefresh(): void {
  clearRefreshTimer()
}

interface LocalDevLoginResponse {
  sessionId?: string
  token?: string
  tokenType?: string
}

let inFlightJwtExchange: Promise<string> | null = null

/**
 * EVP_KDF - OpenSSL-compatible key derivation function
 * Derives key and IV from password and salt using MD5 hashing
 */
async function evpKdf(
  password: string,
  salt: Uint8Array,
  keySize: number,
  ivSize: number
): Promise<{ key: Uint8Array; iv: Uint8Array }> {
  const encoder = new TextEncoder()
  const passwordBytes = encoder.encode(password)

  let derivedKey = new Uint8Array(0)
  let block = new Uint8Array(0)

  while (derivedKey.length < keySize + ivSize) {
    // Concatenate block + password + salt
    const data = new Uint8Array(block.length + passwordBytes.length + salt.length)

    data.set(block, 0)
    data.set(passwordBytes, block.length)
    data.set(salt, block.length + passwordBytes.length)

    // Hash with MD5
    const hashBuffer = await crypto.subtle.digest('MD5', data).catch(() => {
      // Fallback for browsers that don't support MD5 in SubtleCrypto
      return md5Fallback(data)
    })

    block = new Uint8Array(hashBuffer)

    // Append to derived key
    const newDerivedKey = new Uint8Array(derivedKey.length + block.length)

    newDerivedKey.set(derivedKey, 0)
    newDerivedKey.set(block, derivedKey.length)
    derivedKey = newDerivedKey
  }

  return {
    key: derivedKey.slice(0, keySize),
    iv: derivedKey.slice(keySize, keySize + ivSize),
  }
}

/**
 * MD5 implementation for browsers that don't support it in SubtleCrypto
 * Handles arbitrary length inputs correctly (fixed from previous buggy implementation)
 */
function md5Fallback(data: Uint8Array): ArrayBuffer {
  function add32(a: number, b: number) {
    return (a + b) & 0xffffffff
  }

  function cmn(q: number, a: number, b: number, x: number, s: number, t: number) {
    a = add32(add32(a, q), add32(x, t))
    return add32((a << s) | (a >>> (32 - s)), b)
  }

  function ff(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
    return cmn((b & c) | (~b & d), a, b, x, s, t)
  }

  function gg(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
    return cmn((b & d) | (c & ~d), a, b, x, s, t)
  }

  function hh(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
    return cmn(b ^ c ^ d, a, b, x, s, t)
  }

  function ii(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
    return cmn(c ^ (b | ~d), a, b, x, s, t)
  }

  function md5cycle(x: number[], k: number[]) {
    let a = x[0],
      b = x[1],
      c = x[2],
      d = x[3]

    a = ff(a, b, c, d, k[0], 7, -680876936)
    d = ff(d, a, b, c, k[1], 12, -389564586)
    c = ff(c, d, a, b, k[2], 17, 606105819)
    b = ff(b, c, d, a, k[3], 22, -1044525330)
    a = ff(a, b, c, d, k[4], 7, -176418897)
    d = ff(d, a, b, c, k[5], 12, 1200080426)
    c = ff(c, d, a, b, k[6], 17, -1473231341)
    b = ff(b, c, d, a, k[7], 22, -45705983)
    a = ff(a, b, c, d, k[8], 7, 1770035416)
    d = ff(d, a, b, c, k[9], 12, -1958414417)
    c = ff(c, d, a, b, k[10], 17, -42063)
    b = ff(b, c, d, a, k[11], 22, -1990404162)
    a = ff(a, b, c, d, k[12], 7, 1804603682)
    d = ff(d, a, b, c, k[13], 12, -40341101)
    c = ff(c, d, a, b, k[14], 17, -1502002290)
    b = ff(b, c, d, a, k[15], 22, 1236535329)

    a = gg(a, b, c, d, k[1], 5, -165796510)
    d = gg(d, a, b, c, k[6], 9, -1069501632)
    c = gg(c, d, a, b, k[11], 14, 643717713)
    b = gg(b, c, d, a, k[0], 20, -373897302)
    a = gg(a, b, c, d, k[5], 5, -701558691)
    d = gg(d, a, b, c, k[10], 9, 38016083)
    c = gg(c, d, a, b, k[15], 14, -660478335)
    b = gg(b, c, d, a, k[4], 20, -405537848)
    a = gg(a, b, c, d, k[9], 5, 568446438)
    d = gg(d, a, b, c, k[14], 9, -1019803690)
    c = gg(c, d, a, b, k[3], 14, -187363961)
    b = gg(b, c, d, a, k[8], 20, 1163531501)
    a = gg(a, b, c, d, k[13], 5, -1444681467)
    d = gg(d, a, b, c, k[2], 9, -51403784)
    c = gg(c, d, a, b, k[7], 14, 1735328473)
    b = gg(b, c, d, a, k[12], 20, -1926607734)

    a = hh(a, b, c, d, k[5], 4, -378558)
    d = hh(d, a, b, c, k[8], 11, -2022574463)
    c = hh(c, d, a, b, k[11], 16, 1839030562)
    b = hh(b, c, d, a, k[14], 23, -35309556)
    a = hh(a, b, c, d, k[1], 4, -1530992060)
    d = hh(d, a, b, c, k[4], 11, 1272893353)
    c = hh(c, d, a, b, k[7], 16, -155497632)
    b = hh(b, c, d, a, k[10], 23, -1094730640)
    a = hh(a, b, c, d, k[13], 4, 681279174)
    d = hh(d, a, b, c, k[0], 11, -358537222)
    c = hh(c, d, a, b, k[3], 16, -722521979)
    b = hh(b, c, d, a, k[6], 23, 76029189)
    a = hh(a, b, c, d, k[9], 4, -640364487)
    d = hh(d, a, b, c, k[12], 11, -421815835)
    c = hh(c, d, a, b, k[15], 16, 530742520)
    b = hh(b, c, d, a, k[2], 23, -995338651)

    a = ii(a, b, c, d, k[0], 6, -198630844)
    d = ii(d, a, b, c, k[7], 10, 1126891415)
    c = ii(c, d, a, b, k[14], 15, -1416354905)
    b = ii(b, c, d, a, k[5], 21, -57434055)
    a = ii(a, b, c, d, k[12], 6, 1700485571)
    d = ii(d, a, b, c, k[3], 10, -1894986606)
    c = ii(c, d, a, b, k[10], 15, -1051523)
    b = ii(b, c, d, a, k[1], 21, -2054922799)
    a = ii(a, b, c, d, k[8], 6, 1873313359)
    d = ii(d, a, b, c, k[15], 10, -30611744)
    c = ii(c, d, a, b, k[6], 15, -1560198380)
    b = ii(b, c, d, a, k[13], 21, 1309151649)
    a = ii(a, b, c, d, k[4], 6, -145523070)
    d = ii(d, a, b, c, k[11], 10, -1120210379)
    c = ii(c, d, a, b, k[2], 15, 718787259)
    b = ii(b, c, d, a, k[9], 21, -343485551)

    x[0] = add32(a, x[0])
    x[1] = add32(b, x[1])
    x[2] = add32(c, x[2])
    x[3] = add32(d, x[3])
  }

  function bytesToWords(bytes: Uint8Array, offset: number): number[] {
    const words: number[] = []

    for (let i = 0; i < 16; i++) {
      const idx = offset + i * 4

      words[i] =
        bytes[idx] + (bytes[idx + 1] << 8) + (bytes[idx + 2] << 16) + (bytes[idx + 3] << 24)
    }

    return words
  }

  // MD5 requires padding: append 0x80, then zeros, then 64-bit length
  const originalLength = data.length
  const bitLength = originalLength * 8

  // Calculate padded length: must be 64 bytes aligned, with room for 8 bytes length + 1 byte 0x80
  let paddedLength = originalLength + 1 // +1 for 0x80

  while (paddedLength % 64 !== 56) {
    paddedLength++
  }

  paddedLength += 8 // +8 for bit length

  // Create padded message
  const padded = new Uint8Array(paddedLength)

  padded.set(data)
  padded[originalLength] = 0x80

  // Append bit length as 64-bit little-endian (we only use lower 32 bits for simplicity)
  padded[paddedLength - 8] = bitLength & 0xff
  padded[paddedLength - 7] = (bitLength >>> 8) & 0xff
  padded[paddedLength - 6] = (bitLength >>> 16) & 0xff
  padded[paddedLength - 5] = (bitLength >>> 24) & 0xff
  // Upper 32 bits are 0 for messages < 512MB

  // Initialize state
  const state = [1732584193, -271733879, -1732584194, 271733878]

  // Process each 64-byte block
  for (let i = 0; i < paddedLength; i += 64) {
    md5cycle(state, bytesToWords(padded, i))
  }

  // Convert state to bytes (little-endian)
  const result = new Uint8Array(16)

  for (let i = 0; i < 4; i++) {
    result[i * 4] = state[i] & 0xff
    result[i * 4 + 1] = (state[i] >>> 8) & 0xff
    result[i * 4 + 2] = (state[i] >>> 16) & 0xff
    result[i * 4 + 3] = (state[i] >>> 24) & 0xff
  }

  return result.buffer
}

/**
 * Encrypt credentials using AES-256-CBC
 */
async function encryptCredentials(credentials: LoginCredentials, nonce: string): Promise<string> {
  const encoder = new TextEncoder()

  // Generate random 8-byte salt
  const salt = crypto.getRandomValues(new Uint8Array(8))

  // Derive key and IV using EVP_KDF
  const { key, iv } = await evpKdf(nonce, salt, 32, 16)

  // Pass views as BufferSource; some environments reject key.buffer / iv.buffer alone (e.g. jsdom).
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key as BufferSource,
    { name: 'AES-CBC' },
    false,
    ['encrypt']
  )

  // Encrypt the credentials
  // Note: crypto.subtle.encrypt with AES-CBC automatically applies PKCS7 padding
  // Do NOT manually add padding - this caused double-padding bug (matched from page-editor/src/load-test/get-token.js)
  const plaintext = encoder.encode(JSON.stringify(credentials))

  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv: iv as BufferSource },
    cryptoKey,
    plaintext
  )

  // Create OpenSSL prefix format: "Salted__" + salt + encrypted
  const saltedPrefix = encoder.encode('Salted__')
  const result = new Uint8Array(saltedPrefix.length + salt.length + encrypted.byteLength)

  result.set(saltedPrefix, 0)
  result.set(salt, saltedPrefix.length)
  result.set(new Uint8Array(encrypted), saltedPrefix.length + salt.length)

  // Return as base64
  return btoa(String.fromCharCode(...result))
}

/**
 * Fetch nonce from authentication server
 */
async function fetchNonce(): Promise<string> {
  const authApiUrl = getAuthApiUrl()
  const response = await fetch(`${authApiUrl}/auth/nonce`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch nonce: ${response.status} ${response.statusText}`)
  }

  const data: NonceResponse = await response.json()

  return data.payload.nonce
}

/**
 * Perform login with encrypted credentials
 */
async function performLogin(encryptedContent: string, nonce: string): Promise<string> {
  const authApiUrl = getAuthApiUrl()
  const response = await fetch(`${authApiUrl}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      content: encryptedContent,
      nonce,
    }),
  })

  if (!response.ok) {
    const errorText = await response.text()

    throw new Error(`Login failed: ${response.status} ${response.statusText} - ${errorText}`)
  }

  const data: LoginResponse = await response.json()

  return data.payload.authToken
}

function isLocalDevAuthMode(): boolean {
  const apiBaseUrl = getApiBaseUrl()
  const isLocalHost =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

  return isLocalHost && /localhost|127\.0\.0\.1/.test(apiBaseUrl)
}

async function performLocalDevLogin(username: string, password: string): Promise<string> {
  const apiBaseUrl = getApiBaseUrl()
  const response = await fetch(`${apiBaseUrl}/authentication/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ username, password }),
  })

  if (!response.ok) {
    const errorText = await response.text().catch(() => '')

    throw new Error(
      `Local login failed: ${response.status} ${response.statusText}${
        errorText ? ` - ${errorText}` : ''
      }`
    )
  }

  const data: LocalDevLoginResponse = await response.json().catch(() => ({}))

  if (!data.sessionId || !data.token) {
    throw new Error('Local login failed: response did not include sessionId/token')
  }

  storeAuthToken(data.sessionId, username)
  storeJwtToken(data.token)
  return data.sessionId
}

/**
 * Main authentication function
 * @param username - User's username
 * @param password - User's password
 * @returns The authentication token (wpcid)
 */
export async function authenticate(username: string, password: string): Promise<string> {
  logger.info('Starting authentication flow...')

  if (isLocalDevAuthMode()) {
    logger.info('Using local development authentication flow')
    return await performLocalDevLogin(username, password)
  }

  // Step 1: Fetch nonce
  const nonce = await fetchNonce()

  logger.debug('Nonce received')

  // Step 2: Encrypt credentials
  const encryptedContent = await encryptCredentials({ username, password }, nonce)

  logger.debug('Credentials encrypted')

  // Step 3: Perform login
  const authToken = await performLogin(encryptedContent, nonce)

  logger.info('Authentication successful')

  // Step 4: Store token
  storeAuthToken(authToken, username)

  // Step 5: Exchange wpcauth sessionId (wpcid) for JWT bearer token used by Page Editor API
  try {
    await exchangeSessionForJwt(authToken)
  } catch (err) {
    // Avoid leaving app in a "logged in but cannot call API" state
    clearAuth()
    throw err
  }

  return authToken
}

/**
 * Sign in with a yb360 session token handed over by an embedding host (Mary's spread ladder)
 * instead of a username and password. Replaces any session already stored in this tab, then
 * exchanges the token for the Page Editor JWT exactly as `authenticate` does after its login.
 */
export async function adoptHostSession(wpcauth: string, username: string): Promise<void> {
  clearAuth()
  storeAuthToken(wpcauth, username)

  try {
    await exchangeSessionForJwt(wpcauth)
  } catch (err) {
    clearAuth()
    throw err
  }
}

/**
 * Store authentication token in sessionStorage
 */
export function storeAuthToken(token: string, username: string): void {
  sessionStorage.setItem(AUTH_TOKEN_KEY, token)
  sessionStorage.setItem(AUTH_USER_KEY, username)
}

/**
 * Get stored authentication token (sessionStorage, key wpc_auth_token).
 */
export function getStoredAuthToken(): string | null {
  return sessionStorage.getItem(AUTH_TOKEN_KEY)
}

/**
 * Token to send as wpcauth header to yb360/core1 APIs (e.g. bookSetting/all).
 * Tries our storage first, then the old app's localStorage key so we work if
 * the user logged in via the legacy flow or set wpcauth there.
 */
export function getWpcauthForExternalApi(): string | null {
  return getStoredAuthToken() ?? localStorage.getItem('wpcauth')
}

/**
 * Get stored JWT bearer token for Page Editor API calls
 */
export function getStoredJwtToken(): string | null {
  return sessionStorage.getItem(AUTH_JWT_KEY)
}

/**
 * Store JWT bearer token and persist its expiry timestamp.
 */
export function storeJwtToken(token: string): void {
  sessionStorage.setItem(AUTH_JWT_KEY, token)

  const expiryMs = parseJwtExpiryMs(token)

  if (expiryMs) {
    sessionStorage.setItem(AUTH_JWT_EXPIRY_KEY, String(expiryMs))
  }
}

/**
 * Get stored JWT expiry as a Unix-epoch millisecond timestamp, or `null`.
 */
export function getStoredJwtExpiryMs(): number | null {
  const raw = sessionStorage.getItem(AUTH_JWT_EXPIRY_KEY)

  if (!raw) return null
  const n = Number(raw)

  return Number.isFinite(n) ? n : null
}

/**
 * Exchange wpcauth sessionId (wpcid) for JWT bearer token.
 *
 * Swagger: POST /authentication/token { sessionId }
 */
export async function exchangeSessionForJwt(sessionId: string): Promise<string> {
  if (inFlightJwtExchange) return inFlightJwtExchange

  inFlightJwtExchange = (async () => {
    const apiBaseUrl = getApiBaseUrl()
    const response = await fetch(`${apiBaseUrl}/authentication/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ sessionId }),
    })

    if (!response.ok) {
      const errorText = await response.text().catch(() => '')

      throw new Error(
        `Failed to exchange session for JWT: ${response.status} ${response.statusText}${
          errorText ? ` - ${errorText}` : ''
        }`
      )
    }

    const data: JwtTokenResponse = await response.json()
    const { token } = data

    if (!token) {
      throw new Error('Failed to exchange session for JWT: response did not include `token`')
    }

    storeJwtToken(token)
    return token
  })()

  try {
    return await inFlightJwtExchange
  } finally {
    inFlightJwtExchange = null
  }
}

/**
 * Refresh JWT bearer token.
 *
 * Swagger: POST /authentication/refresh with `Authorization: Bearer <token>`
 * Returns a new `token` only when refresh is performed. Within 120 s of the token being minted the
 * backend skips the refresh and answers 200 with only "Current token is still valid"; the current
 * token is returned then, so callers don't treat a still-valid token as a failed refresh and
 * force a logout.
 */
export async function refreshJwtToken(currentToken?: string): Promise<string | null> {
  const jwt = currentToken ?? getStoredJwtToken()

  if (!jwt) return null

  const apiBaseUrl = getApiBaseUrl()
  const response = await fetch(`${apiBaseUrl}/authentication/refresh`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${jwt}`,
    },
  })

  if (!response.ok) {
    return null
  }

  const data: JwtTokenResponse = await response.json().catch(() => ({}))
  const next = data.token

  if (next) {
    storeJwtToken(next)
    scheduleProactiveRefresh(next)
    return next
  }

  return jwt
}

/**
 * Get stored username
 */
export function getStoredUsername(): string | null {
  return sessionStorage.getItem(AUTH_USER_KEY)
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!getStoredAuthToken()
}

/**
 * Clear authentication data (logout)
 */
export function clearAuth(): void {
  stopProactiveRefresh()
  sessionStorage.removeItem(AUTH_TOKEN_KEY)
  sessionStorage.removeItem(AUTH_USER_KEY)
  sessionStorage.removeItem(AUTH_JWT_KEY)
  sessionStorage.removeItem(AUTH_JWT_EXPIRY_KEY)
  clearStoredConnectionId()
  clearStoredEditorZoom()
  clearStoredSpreadIndex()
  clearStoredPhotosTrayAlbum()
  clearStoredPhotosTrayShowFilename()
  clearStoredPhotosTraySort()
  logger.info('Authentication cleared')
}

/**
 * Logout user
 */
export function logout(): void {
  clearAuth()
}
